import { useState, ReactNode } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Theme,
  CircularProgress, 
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Settings,
  LightMode,
  DarkMode,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material'; 
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useThemeMode } from '../contexts/ThemeContext';
import { company } from '../config/branding';
import SidebarNavigation from '../components/navigation/SidebarNavigation';
import { MENU_SECTIONS, type MenuEntry } from '../components/navigation/menuConfig';
import { usePermission } from '../hooks/usePermissions'; 

const DRAWER_WIDTH = 280;
const AVATAR_GRADIENT = 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)';
const PRIMARY_COLOR = '#4a90e2';

const getAvatarStyles = (size: number) => ({
  width: size,
  height: size,
  background: AVATAR_GRADIENT,
  fontWeight: 600,
  fontSize: `${size / 2.8}px`,
  border: '2px solid rgba(255,255,255,0.15)',
  display: 'flex',
  alignItems: 'center',
  justify: 'center',
  color: '#fff',
  textTransform: 'uppercase' as const,
});

const getMenuItemStyles = (theme: Theme, hoverColor: string = PRIMARY_COLOR) => ({
  py: 1.25,
  px: 2,
  mx: 1,
  my: 0.5,
  borderRadius: '8px',
  color: theme.palette.text.primary,
  transition: 'all 0.15s ease',
  '&:hover': {
    bgcolor: `rgba(${hoverColor === PRIMARY_COLOR ? '74,144,226' : '229,115,115'},0.08)`,
    color: hoverColor,
    '& .MuiListItemIcon-root': {
      color: hoverColor,
    },
  },
});

interface MainLayoutProps {
  children: ReactNode;
}

interface UserAvatarProps {
  username?: string;
  size: number;
}

interface UserProfileProps {
  username?: string;
  email?: string;
  onProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  isLoading?: boolean; 
}

interface DrawerHeaderProps {
  mode: 'light' | 'dark';
}

interface ProfileMenuProps {
  anchorEl: HTMLElement | null;
  username?: string;
  email?: string;
  onClose: () => void;
  onNavigate: (path: string) => void;
  onToggleTheme: () => void;    
  onLogout: () => Promise<void>; 
}

interface ToggleButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

const UserAvatar = ({ username, size }: UserAvatarProps) => {
  const initial = username && username.trim()
    ? username.trim().charAt(0).toUpperCase()
    : 'U';

  return (
    <Avatar sx={getAvatarStyles(size)}>
      {initial}
    </Avatar>
  );
};

const UserProfile = ({ username, email, onProfileMenuOpen, isLoading }: UserProfileProps) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  return (
    <Box
      sx={{
        p: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
        background: mode === 'dark' ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.02)',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          borderRadius: '12px',
          background: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          border: `1px solid ${theme.palette.divider}`,
          cursor: isLoading ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': !isLoading ? {
            background: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)',
          } : {},
        }}
        onClick={isLoading ? undefined : onProfileMenuOpen}
      >
        {isLoading ? (
          <CircularProgress size={24} sx={{ color: PRIMARY_COLOR, mx: 1 }} />
        ) : (
          <UserAvatar username={username} size={42} />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isLoading ? 'Chargement...' : (email || username || 'Utilisateur')}
          </Typography>
        </Box>

        {!isLoading && <Settings sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />}
      </Box>
    </Box>
  );
};

const DrawerHeader = ({ mode }: DrawerHeaderProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: '72px',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            background: AVATAR_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '1.2rem',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(74,144,226,0.25)',
          }}
        >
          {company.name.charAt(0)}
        </Box>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1rem', lineHeight: 1.2 }}>
            {company.name}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
            Application Pro
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ProfileMenu = ({ anchorEl, username, email, onClose, onNavigate, onToggleTheme, onLogout }: ProfileMenuProps) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  return (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={Boolean(anchorEl)}
      onClose={onClose}
      onClick={onClose}
      TransitionComponent={Fade}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: mode === 'dark' ? '0 4px 16px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
          mt: 1.5,
          minWidth: 260,
          borderRadius: 0,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <UserAvatar username={username} size={44} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: '0.95rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {username || 'Utilisateur'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ py: 0.5 }}>
        <MenuItem onClick={() => onNavigate('/settings')} sx={getMenuItemStyles(theme)}>
          <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: 38, transition: 'color 0.15s ease' }}>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Paramètres"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleTheme} sx={getMenuItemStyles(theme)}>
          <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: 38, transition: 'color 0.15s ease' }}>
            {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={mode === 'dark' ? 'Mode clair' : 'Mode sombre'}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
          />
        </MenuItem>

        <Divider sx={{ borderColor: theme.palette.divider, opacity: 0.3, my: 0.5, mx: 2 }} />

        <MenuItem onClick={onLogout} sx={getMenuItemStyles(theme, '#e57373')}>
          <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: 38, transition: 'color 0.15s ease' }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Se déconnecter"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
          />
        </MenuItem>
      </Box>
    </Menu>
  );
};

const ToggleButton = ({ isOpen, onClick }: ToggleButtonProps) => {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        display: { xs: 'none', sm: 'flex' },
        position: 'fixed',
        left: isOpen ? `${DRAWER_WIDTH}px` : '0px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 28,
        height: 56,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderTopRightRadius: '28px',
        borderBottomRightRadius: '28px',
        boxShadow: 'none',
        transition: 'left 0.3s ease, background-color 0.2s ease',
        zIndex: 1201,
        cursor: 'pointer',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          bgcolor: theme.palette.action.hover,
          '& .toggle-icon': {
            color: theme.palette.primary.main,
          },
        },
      }}
    >
      {isOpen ? (
        <ChevronLeft
          className="toggle-icon"
          sx={{
            fontSize: '1.5rem',
            color: theme.palette.text.secondary,
            transition: 'color 0.3s ease',
          }}
        />
      ) : (
        <ChevronRight
          className="toggle-icon"
          sx={{
            fontSize: '1.5rem',
            color: theme.palette.text.secondary,
            transition: 'color 0.3s ease',
          }}
        />
      )}
    </Box>
  );
};

const findLabelInEntries = (
  entries: MenuEntry[],
  pathname: string
): string | null => {
  for (const entry of entries) {
    if (entry.kind === 'leaf' && entry.path === pathname) {
      return entry.label;
    }

    if (entry.kind === 'group') {
      const found = findLabelInEntries(entry.children, pathname);
      if (found) return found;
    }
  }

  return null;
};

const MainLayout = ({ children }: MainLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const { user: apiUser, isLoading } = usePermission();
  
  const user = {
    username: apiUser?.email ? apiUser.email.split('@')[0] : 'Utilisateur',
    email: apiUser?.email,
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleTheme } = useThemeMode();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDesktopDrawerToggle = () => setDesktopOpen(!desktopOpen);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleProfileMenuClose();
    if (isMobile) setMobileOpen(false);
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '200px',
          background: 'radial-gradient(circle at 50% 0%, rgba(100,150,250,0.08), transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <DrawerHeader mode={mode} />
      <SidebarNavigation onNavigate={handleNavigation} />
      
      <UserProfile
        username={user?.username}
        email={user?.email} 
        onProfileMenuOpen={handleProfileMenuOpen}
        isLoading={isLoading} // 🌟 Transmet l'état de chargement pour un rendu fluide
      />
    </Box>
  );

  const activePageTitle = (() => {
    for (const section of MENU_SECTIONS) {
      const found = findLabelInEntries(section.entries, location.pathname);
      if (found) return found;
    }
    return company.name;
  })();

  // 🌟 Au lieu de bloquer l'application entière, on charge le Layout global 
  // mais on met un Loader central dans la zone de contenu principale si l'utilisateur n'est pas encore là.
  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', backgroundColor: theme.palette.background.default, position: 'relative' }}>
      <CssBaseline />
      {mode === 'dark' && (
        <Box aria-hidden sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', opacity: 0.06,
          backgroundImage: 'repeating-linear-gradient(90deg, rgba(217,217,217,0.35) 0px, rgba(217,217,217,0.35) 1px, transparent 1px, transparent 20px), repeating-linear-gradient(0deg, rgba(217,217,217,0.35) 0px, rgba(217,217,217,0.35) 1px, transparent 1px, transparent 20px)'
        }} />
      )}

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { sm: 'none' },
          bgcolor: mode === 'dark' ? 'rgba(46,48,110,0.95)' : 'rgba(255,255,255,0.95)',
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.05)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              borderRadius: '12px',
              '&:hover': { bgcolor: 'rgba(217,217,217,0.1)' },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              fontSize: '1.1rem',
              letterSpacing: '-0.02em',
            }}
          >
            {activePageTitle}
          </Typography>

          <IconButton
            onClick={handleProfileMenuOpen}
            disabled={isLoading}
            sx={{
              p: 0,
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
            aria-controls="account-menu"
            aria-haspopup="true"
          >
            <UserAvatar username={user?.username} size={38} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <ProfileMenu
        anchorEl={anchorEl}
        username={user?.username}
        email={user?.email}
        onClose={handleProfileMenuClose}
        onNavigate={handleNavigation}
        onToggleTheme={toggleTheme}
        onLogout={handleLogout}
      />

      <Box
        component="nav"
        sx={{
          width: { sm: desktopOpen ? DRAWER_WIDTH : 0 },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: 'none',
              boxShadow: '4px 0 16px rgba(0,0,0,0.15)',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="persistent"
          open={desktopOpen}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: 'none',
              boxShadow: '4px 0 16px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <ToggleButton isOpen={desktopOpen} onClick={handleDesktopDrawerToggle} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: desktopOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          height: '100dvh',
          backgroundColor: 'transparent',
          position: 'relative',
          zIndex: 1,
          transition: 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <Toolbar sx={{ minHeight: '64px !important' }} />
        </Box>

        <Box sx={{ p: { xs: 1.5, md: 2, lg: 3 }, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              position: 'relative',
              background: theme.palette.background.paper,
              p: { xs: 2, md: 3, lg: 3.5 },
              flexGrow: 1,
              minHeight: 0,
              boxShadow: mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.08)',
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #4a90e2 0%, #357abd 100%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: isLoading ? 'center' : 'flex-start', alignItems: isLoading ? 'center' : 'stretch' }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={40} sx={{ color: PRIMARY_COLOR }} />
                  <Typography variant="body2" color="text.secondary">
                    Chargement de l'espace de travail...
                  </Typography>
                </Box>
              ) : (
                children
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;